/*
 * @Author: Claude
 * @Date: 2026-01-06
 * @Description: 图片宽高比校验
 */
import { t } from '@/utils/helper/intl';

import { FileUploadRule } from '../types/validator';
import { getImageDimensions } from '../utils/getImageDimensions';
import {
  formatAspectRatio,
  formatAspectRatioAsFraction,
  parseAspectRatio,
} from '../utils/parseAspectRatio';

/**
 * 校验图片宽高比
 * @param file - 待校验的文件
 * @param rule - 校验规则
 * @returns Promise<string | null> - 错误信息或 null
 */
export const validateImageAspectRatio = async (
  file: File,
  rule: FileUploadRule,
): Promise<string | null> => {
  const { minAspectRatio, maxAspectRatio, message } = rule;

  // 如果没有设置任何宽高比相关的规则，跳过校验
  if (!minAspectRatio && !maxAspectRatio) {
    return null;
  }

  try {
    const { width, height } = await getImageDimensions(file);

    // 避免除以零
    if (height === 0) {
      return t('merror.httpError.12301000'); // 无法读取图片尺寸
    }

    const currentRatio = width / height;

    // 1. 校验最小宽高比
    if (minAspectRatio) {
      try {
        const minRatio = parseAspectRatio(minAspectRatio);

        if (currentRatio < minRatio) {
          return (
            message ||
            t('minRatio-currentRatio-width-height', {
              minRatio: formatAspectRatio(minRatio),
              minRatioFraction: formatAspectRatioAsFraction(minRatio),
              currentRatio: formatAspectRatio(currentRatio),
              currentRatioFraction: formatAspectRatioAsFraction(currentRatio),
              width,
              height,
            })
          );
        }
      } catch (error) {
        // 解析失败，返回原始值作为错误信息
        return `Invalid minAspectRatio format: ${minAspectRatio}`;
      }
    }

    // 2. 校验最大宽高比
    if (maxAspectRatio) {
      try {
        const maxRatio = parseAspectRatio(maxAspectRatio);

        if (currentRatio > maxRatio) {
          return (
            message ||
            t('maxRatio-currentRatio-width-height', {
              maxRatio: formatAspectRatio(maxRatio),
              maxRatioFraction: formatAspectRatioAsFraction(maxRatio),
              currentRatio: formatAspectRatio(currentRatio),
              currentRatioFraction: formatAspectRatioAsFraction(currentRatio),
              width,
              height,
            })
          );
        }
      } catch (error) {
        // 解析失败，返回原始值作为错误信息
        return `Invalid maxAspectRatio format: ${maxAspectRatio}`;
      }
    }

    return null;
  } catch (error) {
    return `${error instanceof Error ? error.message : t('merror.httpError.12301000')}`;
  }
};
